/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2022, Zumper
 */

import type { ComponentStyleConfig } from '@chakra-ui/theme'
import { mode } from '@chakra-ui/theme-tools'

import { focus } from '../sharedStyles'

const tabsDisabledState = {
  _disabled: {
    color: 'gray.600',
    cursor: 'not-allowed',
    opacity: 0.5,
    _hover: {
      color: 'gray.600',
      borderBottomColor: 'transparent',
      opacity: 0.5,
    },
  },
}

export const Z4Tabs: ComponentStyleConfig = {
  parts: ['root', 'tablist', 'tab', 'tabpanel'],
  baseStyle: {
    root: {
      fontFamily: 'body',
    },
    tab: {
      bg: 'transparent',
      cursor: 'pointer',
      border: 0,
      fontFamily: 'body',
      fontWeight: 'medium',
      color: 'gray.950',
      borderBottom: 'solid 2px',
      flexGrow: { base: 1, md: 0 },
      _focus: {
        outline: 'none',
        boxShadow: 'none',
      },
      _focusVisible: focus,
      ...tabsDisabledState,
    },
    tablist: {
      // https://png-pixel.com gray.150 (#EBEAE6)
      background:
        'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/erZfwAJPwO8O4sMFAAAAABJRU5ErkJggg==) repeat-x bottom',
      borderBottom: 'none',
      overflowX: 'auto',
      overflowY: 'hidden',
      position: 'relative',
      scrollbarWidth: 'none',
      whiteSpace: 'nowrap',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    indicator: {
      bg: 'pink.600',
      borderRadius: '1px',
      bottom: 0,
      height: '2px',
    },
  },
  sizes: {
    sm: {
      tab: {
        pt: 2,
        pb: 2,
        px: 0,
        mx: 3,
        _first: {
          ml: 0,
        },
        _last: {
          mr: 0,
        },
      },
      tabpanel: {
        px: 0,
      },
    },
    md: {
      tab: {
        pt: 3,
        pb: 4,
        px: 0,
        mx: 3,
        _first: {
          ml: 0,
        },
        _last: {
          mr: 0,
        },
      },
      tabpanel: {
        px: 0,
      },
    },
    lg: {
      tab: {
        pt: 3,
        pb: 4,
        px: 0,
        mx: 3,
        _first: {
          ml: 0,
        },
        _last: {
          mr: 0,
        },
      },
      tabpanel: {
        px: 0,
      },
    },
  },
  variants: {
    line: (props: any) => ({
      tablist: {
        // https://png-pixel.com gray.150 (#EBEAE6)
        background:
          'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/erZfwAJPwO8O4sMFAAAAABJRU5ErkJggg==) repeat-x bottom',
        borderBottom: 'none',
      },
      tab: {
        borderBottom: 'none',
        color: mode('gray.600', 'gray.0')(props),
        position: 'relative',
        _hover: {
          color: mode('gray.950', 'gray.0')(props),
        },
        _selected: {
          color: mode('gray.950', 'gray.0')(props),
        },
        ...tabsDisabledState,
      },
      tabpanel: {
        color: mode('gray.950', 'gray.0')(props),
      },
    }),
    'no-underline': (props: any) => ({
      tablist: {
        backgroundImage: 'none',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
      },
      tab: {
        color: mode('gray.800', 'gray.0')(props),
        borderBottomColor: 'transparent',
        letterSpacing: '0.3px',
        _hover: {
          borderBottomColor: 'pink.600',
          color: mode('gray.950', 'gray.0')(props),
        },
        _selected: {
          borderBottomColor: 'pink.600',
          color: mode('gray.950', 'gray.0')(props),
          fontWeight: 'bold',
          letterSpacing: '-0.4px',
        },
        ...tabsDisabledState,
      },
      tabpanel: {
        color: mode('gray.950', 'gray.0')(props),
      },
    }),
    'no-underline-dark': {
      tablist: {
        backgroundImage: 'none',
        borderBottomWidth: 2,
        borderBottomColor: 'transparent',
      },
      tab: {
        color: 'gray.0',
        borderBottomColor: 'transparent',
        letterSpacing: '0.14px',
        _hover: {
          borderBottomColor: 'pink.600',
          color: 'gray.0',
        },
        _selected: {
          fontWeight: 'bold',
          letterSpacing: '-0.41px',
          borderBottomColor: 'pink.600',
          color: 'gray.0',
        },
        ...tabsDisabledState,
      },
      tabpanel: {
        color: 'gray.0',
      },
    },
  },
  defaultProps: {},
}
